import { createPortal } from 'react-dom';
import { PenLine } from 'lucide-react';

interface ClaimedNoticeDialogProps {
  memberName: string;
  leaderName: string;
  /** Minutes since the other leader started, when known. */
  startedMinutesAgo?: number;
  onClose: () => void;
}

function startedPhrase(minutes: number | undefined): string {
  if (minutes === undefined) return '';
  const when = minutes <= 0 ? 'just now' : `${minutes} minutes ago`;
  return `They started ${when}. `;
}

/** "Rabbi Cohen is writing to Sarah". No override: just "Got it". */
export function ClaimedNoticeDialog({
  memberName,
  leaderName,
  startedMinutesAgo,
  onClose,
}: ClaimedNoticeDialogProps) {
  const first = memberName.trim().split(/\s+/)[0] || memberName;
  const startedText = startedPhrase(startedMinutesAgo);
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${leaderName} is writing to ${first}`}
        className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-5 text-center"
      >
        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
          <PenLine size={22} className="text-emerald-600" />
        </div>
        <h3 className="mt-3 text-lg font-semibold text-slate-800 font-['Merriweather',serif]">
          {leaderName} is writing to {first}
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          {startedText}
          So {first} gets one warm hello, not two. This one is theirs.
        </p>
        <p className="mt-2 text-xs text-slate-400">
          If it isn&apos;t sent within 30 minutes, {first}&apos;s card opens up again.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full py-3 rounded-full text-white font-semibold bg-gradient-to-r from-[#8e4585] to-[#63345e]"
        >
          Got it
        </button>
      </div>
    </div>,
    document.body
  );
}
